import React, {
  useCallback,
  useEffect
} from "react";
import styled, { css } from "styled-components";
import _ from "lodash";
import { CellContext } from "@tanstack/react-table";

import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import Grid from "src/library/components/Hedron";
import { Text } from "src/library/components/Text";
import { Input } from "src/library/components/Forms";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import useUserGroup from "src/core/users/hooks/useUserGroup";
import {
  CreateEditUserGroup,
  IUser
} from "src/core/users/api/users.api";
import { ITableInstance } from "src/library/components/Tables/Table";
import { SelectUsersModel } from "src/core/users/components/SelectUsersModel";
import SelectedUser from "src/core/users/components/SelectedUser";
import { getUserTableColumns } from "src/core/shared/components/tables/usersTableColumns";
import { PaginatedParams } from "src/library/utils";
import { Table } from "src/core/dashboard/components/widgets";

interface UrlParams {
  userGroupId: string
}

const defaultPagination: PaginatedParams<IUser> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "firstName", "ASC" ] ]
};

/**
 * Page used by admins to create or edit a user group
 */
const CreateEditUserGroupPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = ({ title, ...props }) => {
  const {
    match, history, terminology
  } = props;

  const userGroupId = match ? parseInt(match.params.userGroupId, 10) : undefined;
  const checkPermission = useCheckUserPermission();
  const canAmendUserGroup = checkPermission("amend-user");
  const TableRef = React.useRef<ITableInstance<IUser>>(null);
  const [ showModal, setShowModal ] = React.useState(false);
  const [ selectedManager, setSelectedManager ] = React.useState<IUser | null>(null);

  const {
    isLoading, userGroup, setCurrentUserGroup, createUserGroupReq, updateUserGroupReq, getUserGroupReq
  } = useUserGroup(history, userGroupId);

  const fetchData = React.useCallback(() => {
    if (userGroup?.id) {
      getUserGroupReq({ id: userGroup.id });
    }
  }, [ getUserGroupReq, userGroup?.id ]);

  // used to inform users select model who already is selected
  const userGroupUserIds: string[] = React.useMemo(() => {
    if (!userGroup?.userGroupUsers || userGroup.userGroupUsers.length === 0) {
      return [];
    }

    return userGroup.userGroupUsers.map(uGU => uGU.userId.toString());
  }, [ userGroup ]);

  // confirm user data to satisfy tableColumns format
  const tableUserGroupUsersData = React.useMemo(() => {
    if (!userGroup?.userGroupUsers) return [];

    return userGroup.userGroupUsers.map(userGroup => userGroup.user);
  }, [ userGroup?.userGroupUsers ]);

  const data = React.useMemo(() => _.compact(tableUserGroupUsersData) ?? [], [ tableUserGroupUsersData ]);

  const handleSave = useCallback((userGroupToSave: CreateEditUserGroup) => {
    if (userGroupToSave.id) {
      updateUserGroupReq(userGroupToSave);
    } else {
      createUserGroupReq(userGroupToSave);
    }
  }, [ createUserGroupReq, updateUserGroupReq ]);

  useEffect(() => {
    if (userGroup?.id) {
      setShowModal(false);
    }
  }, [ isLoading.update, userGroup?.id ]);

  useEffect(() => {
    if (userGroup?.manager) {
      setSelectedManager(userGroup.manager);
    }
  }, [ userGroup?.manager ]);

  const generateActions = useCallback((info: CellContext<IUser, unknown>) => {
    const actions = [
      {
        title: "View user",
        action: () => history.push(`/admin/users/${info.row.original.id}`)
      }
    ];

    if (canAmendUserGroup && userGroup) {
      actions.push({
        title: "Remove",
        action: () => {
          if (userGroup.userGroupUsers) {
            updateUserGroupReq({
              ...userGroup,
              userGroupUsers: _.remove(userGroup.userGroupUsers, ugu => ugu.userId !== info.row.original.id)
            });
          }
        }
      });
    }

    return actions;
  }, [
    canAmendUserGroup,
    history,
    updateUserGroupReq,
    userGroup
  ]);

  const columns = React.useMemo(() => {
    return getUserTableColumns({
      terminology,
      actionButton: info => (
        <ActionButton
          title="Actions"
          items={generateActions(info)}
        />
      ),
      removeColumns: [ "defaultCustomer", "defaultDrop" ]
    });
  }, [ generateActions, terminology ]);

  return (
    <Layout
      title={title}
      loading={isLoading.get && !userGroup}
      {...props}
    >
      <MainContentWrapper fill>
        <StyledCreateEditWrapper>
          {userGroup && (
            <>
              <Text as="h4">
                User Group Name
              </Text>

              <Input
                as="input"
                name="label"
                autoHideLabels
                label=""
                value={userGroup.name}
                disabled={!canAmendUserGroup}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setCurrentUserGroup({
                    ...userGroup,
                    name: event.target.value
                  });
                }}
              />

              <div className="field">
                <Text as="h4">
                  Manager:
                </Text>

                <SelectedUser
                  user={selectedManager}
                  updateUser={setSelectedManager}
                  terminology={terminology}
                  disabled={!canAmendUserGroup}
                />
              </div>

              {canAmendUserGroup && (
                <div className="field">
                  <Button
                    $buttonType="primary"
                    onClick={() => {
                      // already checked if can amend user group before displaying save option
                      handleSave({
                        ...userGroup,
                        managerId: selectedManager?.id || null
                      });
                    }}
                    loading={isLoading.create || isLoading.update}
                  >
                    {userGroup?.id ? "Save" : "Create Group"}
                  </Button>
                </div>
              )}

              {userGroup?.id && (
                <div className="field associated-users">
                  <Text as="h3">
                    Associated Users
                  </Text>

                  <Text>
                    The table below lists all users associated with this group.
                  </Text>

                  {canAmendUserGroup && (
                    <Button
                      $buttonType="primary"
                      onClick={() => setShowModal(true)}
                      className="add-users-button"
                    >
                      Add Users
                    </Button>
                  )}

                  <Table
                    ref={TableRef}
                    mainAction={row => history.push(`/admin/users/${row.original.id}`)}
                    tableOf={terminology.user.plural.text}
                    loading={isLoading.create || isLoading.update || isLoading.get}
                    data={data}
                    onFetchData={fetchData}
                    defaultPagination={defaultPagination}
                    columns={columns}
                  />
                </div>
              )}
            </>
          )}

          {userGroup?.id && (
            <SelectUsersModel
              open={showModal}
              onSave={userIds => {
                const updatedUserGroupUsers = userIds.map(userId => ({
                  userId,
                  userGroupId: userGroup.id as number
                }));

                if (userGroup.userGroupUsers) {
                  updatedUserGroupUsers.push(...userGroup.userGroupUsers);
                }

                updateUserGroupReq({
                  ...userGroup,
                  managerId: selectedManager?.id || null,
                  userGroupUsers: updatedUserGroupUsers
                });
              }}
              alreadySelectedUserIds={userGroupUserIds}
              onDismiss={() => {
                setShowModal(false);
              }}
            />
          )}

        </StyledCreateEditWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

const StyledCreateEditWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    flex-direction: column;
    padding: ${theme.spacing.padding.xs};

    .associated-users {
      margin-top: ${theme.spacing.margins.lg};
      padding-top: ${theme.spacing.margins.lg};
      border-top: 1px solid ${theme.colors.lightGrey};
    }

    .add-users-button {
      margin: ${theme.spacing.margins.lg} 0;
    }

    .field {
      padding-top: ${theme.spacing.padding.lg};

      .product-search {
        margin: ${theme.spacing.padding.lg} 0;
      }
    }
  `}
`;

export default CreateEditUserGroupPage;
